import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import { useKurs } from '@/hooks/useKurs';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

const TIPE_OPTIONS = [
  { value: 'botol', label: 'Botol' },
  { value: 'tutup', label: 'Tutup' },
  { value: 'spray', label: 'Spray' },
  { value: 'fullset', label: 'Fullset' },
];

const SPRAY_TYPES = ['Biasa', 'Continuous'];

const ENTITY_MAP = {
  botol: base44.entities.Botol,
  tutup: base44.entities.Tutup,
  spray: base44.entities.Spray,
  fullset: base44.entities.Fullset,
};

export default function PackagingForm({ item, tipeAwal = 'botol', onClose, onSaved, hidePrices = false }) {
  const [tipe, setTipe] = useState(item?.tipe_packaging ?? tipeAwal);
  const [dariClient, setDariClient] = useState(item?.dari_client || false);
  const { kurs, kursEuro } = useKurs();

  // Fetch packaging items to get vendor list
  const { data: allBotol = [] } = useQuery({ queryKey: ['botol-all'], queryFn: () => base44.entities.Botol.list() });
  const { data: allTutup = [] } = useQuery({ queryKey: ['tutup-all'], queryFn: () => base44.entities.Tutup.list() });
  const { data: allSpray = [] } = useQuery({ queryKey: ['spray-all'], queryFn: () => base44.entities.Spray.list() });
  const { data: allFullset = [] } = useQuery({ queryKey: ['fullset-all'], queryFn: () => base44.entities.Fullset.list() });

  const vendorOptions = useMemo(() => {
    const map = new Set();
    [...allBotol, ...allTutup, ...allSpray, ...allFullset].forEach(b => {
      if (b.vendor && !b.dari_client) map.add(b.vendor.trim());
    });
    return Array.from(map).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [allBotol, allTutup, allSpray, allFullset]);

  const defaultForm = {
    nama: '',
    vendor: '',
    ukuran_label_ml: '',
    ukuran_aktual_ml: '',
    harga_rupiah: '',
    harga_jual: '',
    harga_dollar: '',
    harga_euro: '',
    mata_uang: 'USD',
    catatan_teknis: '',
    catatan: '',
    stok: '',
    tipe_spray: 'Biasa',
    status: 'aktif',
    client_owner: '',
  };

  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (item) {
      setForm({
        nama: item.nama ?? '',
        vendor: item.vendor ?? item.supplier ?? '',
        ukuran_label_ml: item.ukuran_label_ml ?? item.ukuran_label ?? item.ukuran ?? '',
        ukuran_aktual_ml: item.ukuran_aktual_ml ?? item.ukuran_aktual ?? '',
        harga_rupiah: item.harga_rupiah ?? '',
        harga_jual: item.harga_jual ?? '',
        harga_dollar: item.harga_dollar ?? '',
        harga_euro: item.harga_euro ?? '',
        mata_uang: item.mata_uang ?? (item.harga_euro ? 'EUR' : 'USD'),
        catatan_teknis: item.catatan_teknis ?? '',
        catatan: item.catatan ?? '',
        stok: item.stok ?? '',
        tipe_spray: item.tipe ?? 'Biasa',
        status: item.status ?? 'aktif',
        client_owner: item.client_owner ?? '',
      });
    }
  }, [item]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // USD → Rupiah otomatis
  useEffect(() => {
    if (form.mata_uang === 'USD' && form._lastEdited === 'valas' && form.harga_dollar && kurs) {
      setForm(p => ({ ...p, harga_rupiah: (parseFloat(p.harga_dollar) * kurs).toFixed(0) }));
    }
  }, [form.harga_dollar, form.mata_uang, kurs]);

  // EUR → Rupiah otomatis
  useEffect(() => {
    if (form.mata_uang === 'EUR' && form._lastEdited === 'valas' && form.harga_euro && kursEuro) {
      setForm(p => ({ ...p, harga_rupiah: (parseFloat(p.harga_euro) * kursEuro).toFixed(0) }));
    }
  }, [form.harga_euro, form.mata_uang, kursEuro]);

  // Rupiah → USD/EUR otomatis
  useEffect(() => {
    if (form._lastEdited === 'rupiah' && form.harga_rupiah) {
      if (form.mata_uang === 'USD' && kurs) {
        setForm(p => ({ ...p, harga_dollar: (parseFloat(p.harga_rupiah) / kurs).toFixed(4) }));
      } else if (form.mata_uang === 'EUR' && kursEuro) {
        setForm(p => ({ ...p, harga_euro: (parseFloat(p.harga_rupiah) / kursEuro).toFixed(4) }));
      }
    }
  }, [form.harga_rupiah, form.mata_uang, kurs, kursEuro]);

  const entity = ENTITY_MAP[tipe];

  const mutation = useMutation({
    mutationFn: async (data) => {
      if (item) {
        await entity.update(item.id, data);
        if (item.nama && data.nama && item.nama.trim() !== data.nama.trim()) {
          try {
            const oldName = item.nama.trim();
            const newName = data.nama.trim();
            const dbList = await base44.entities.DatabaseBahan.list();
            for (const db of dbList) {
              let changed = false;
              const updatePayload = {};
              if (tipe === 'botol' && db.botol_nama === oldName) {
                updatePayload.botol_nama = newName;
                changed = true;
              } else if (tipe === 'tutup' && db.tutup_nama === oldName) {
                updatePayload.tutup_nama = newName;
                changed = true;
              } else if (tipe === 'spray' && db.spray_nama === oldName) {
                updatePayload.spray_nama = newName;
                changed = true;
              } else if (tipe === 'fullset' && db.fullset_nama === oldName) {
                updatePayload.fullset_nama = newName;
                changed = true;
              }
              if (changed) {
                await base44.entities.DatabaseBahan.update(db.id, updatePayload);
              }
            }
          } catch (err) {
            console.error('Failed to cascade update Packaging in DatabaseBahan:', err);
          }
        }
      } else {
        await entity.create(data);
      }
    },
    onSuccess: onSaved,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const base = {
      nama: form.nama,
      vendor: form.vendor,
      harga_rupiah: parseFloat(form.harga_rupiah) || 0,
      harga_jual: form.harga_jual ? parseFloat(form.harga_jual) : null,
      harga_dollar: form.mata_uang === 'USD' && form.harga_dollar ? parseFloat(form.harga_dollar) : null,
      harga_euro: form.mata_uang === 'EUR' && form.harga_euro ? parseFloat(form.harga_euro) : null,
      mata_uang: form.mata_uang,
      stok: form.stok ? parseInt(form.stok) : undefined,
      status: form.status,
      dari_client: dariClient,
      client_owner: dariClient ? form.client_owner : '',
    };

    if (tipe === 'botol') {
      mutation.mutate({
        ...base,
        ukuran_label_ml: parseFloat(form.ukuran_label_ml) || undefined,
        ukuran_aktual_ml: form.ukuran_aktual_ml ? parseFloat(form.ukuran_aktual_ml) : undefined,
        catatan_teknis: form.catatan_teknis || undefined,
      });
    } else if (tipe === 'tutup') {
      mutation.mutate({ ...base, catatan: form.catatan || undefined });
    } else if (tipe === 'spray') {
      mutation.mutate({ ...base, tipe: form.tipe_spray });
    } else if (tipe === 'fullset') {
      mutation.mutate({
        ...base,
        ukuran_label: form.ukuran_label_ml ? String(form.ukuran_label_ml) : undefined,
        ukuran_aktual: form.ukuran_aktual_ml ? String(form.ukuran_aktual_ml) : undefined,
        ukuran: form.ukuran_label_ml ? String(form.ukuran_label_ml) : undefined,
        catatan: form.catatan || undefined,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 ">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className={`flex items-center justify-between p-5 border-b border-border ${tipe === 'fullset' ? 'bg-emerald-50/50' : ''}`}>
          <div>
            <h3 className="font-display font-semibold text-lg">
              {item ? (tipe === 'fullset' ? 'Edit Fullset Packaging' : 'Edit Packaging') : (tipe === 'fullset' ? 'Tambah Fullset Baru' : 'Tambah Packaging')}
            </h3>
            {tipe === 'fullset' && (
              <p className="text-xs text-muted-foreground mt-0.5">Gabungan Botol, Tutup, dan Spray dengan harga paket</p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Tipe selector — hanya untuk tambah baru */}
          {!item && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Tipe Packaging *</label>
              <div className="flex gap-2">
                {TIPE_OPTIONS.map(t => (
                  <button key={t.value} type="button" onClick={() => setTipe(t.value)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${tipe === t.value ? (t.value === 'fullset' ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' : 'bg-accent text-accent-foreground border-accent') : 'border-border text-muted-foreground hover:border-accent/50'}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Kepemilikan Barang</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setDariClient(false)} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${!dariClient ? 'bg-accent text-accent-foreground border-accent shadow-sm' : 'border-border text-muted-foreground hover:border-accent/50'}`}>Inventori Satala</button>
              <button type="button" onClick={() => { setDariClient(true); setForm(p => ({ ...p, harga_rupiah: 0, harga_jual: 0, harga_dollar: '', harga_euro: '' })); }} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${dariClient ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm' : 'border-border text-muted-foreground hover:border-emerald-500/50'}`}>Titipan Client</button>
            </div>
          </div>

          {dariClient && (
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 mt-2">
              <label className="text-xs font-bold text-emerald-800 mb-1 block">Nama Client / Brand Titipan *</label>
              <Input 
                value={form.client_owner} 
                onChange={e => setForm(p => ({...p, client_owner: e.target.value}))} 
                placeholder="Misal: Dermatech, atau Klinik Bunga..." 
                className="border-emerald-200 focus-visible:ring-emerald-500 bg-white"
              />
              <p className="text-[10px] text-emerald-600 mt-1">Isi dengan nama brand klien secara persis agar tercatat di Data Klien mereka.</p>
            </div>
          )}

          {!dariClient && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Vendor / Supplier</label>
              <SearchableCombobox
                value={form.vendor}
                onValueChange={v => set('vendor', v)}
                options={vendorOptions}
                placeholder="Pilih atau ketik vendor baru..."
                searchPlaceholder="Cari vendor..."
                allowCustom={true}
              />
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              {tipe === 'fullset' ? 'Nama Paket Fullset *' : 'Nama *'}
            </label>
            <Input 
              value={form.nama} 
              onChange={e => set('nama', e.target.value)} 
              placeholder={
                tipe === 'fullset' ? 'Contoh: Set Botol Kaca 30ml + Pump Gold' :
                tipe === 'botol' ? 'Contoh: Botol Kaca Slim 30ml' : 
                tipe === 'tutup' ? 'Contoh: Tutup Gold Magnetic' : 
                'Contoh: Spray Aluminium'
              } 
              required 
            />
          </div>

          {/* Field Ukuran untuk Botol dan Fullset */}
          {(tipe === 'botol' || tipe === 'fullset') && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Ukuran Label (ml) {tipe === 'botol' ? '*' : ''}</label>
                  <Input type="number" value={form.ukuran_label_ml} onChange={e => set('ukuran_label_ml', e.target.value)} placeholder="Misal: 30" required={tipe === 'botol'} />
                  {tipe === 'fullset' && <p className="text-[9px] text-slate-400 mt-1">Ukuran yang tertera pada kemasan</p>}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Ukuran Aktual (ml)</label>
                  <Input type="number" value={form.ukuran_aktual_ml} onChange={e => set('ukuran_aktual_ml', e.target.value)} placeholder="Misal: 33" />
                  {tipe === 'fullset' && <p className="text-[9px] text-slate-400 mt-1">Kapasitas maksimal sesungguhnya</p>}
                </div>
              </div>
              {tipe === 'botol' && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Catatan Teknis</label>
                  <Input value={form.catatan_teknis} onChange={e => set('catatan_teknis', e.target.value)} placeholder="Contoh: rawan rembes jika diisi full" />
                </div>
              )}
            </>
          )}

          {/* Field khusus Spray */}
          {tipe === 'spray' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Tipe Spray *</label>
              <div className="flex gap-2">
                {SPRAY_TYPES.map(t => (
                  <button key={t} type="button" onClick={() => set('tipe_spray', t)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${form.tipe_spray === t ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:border-accent/50'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Currency selector */}
          {!dariClient && !hidePrices && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Acuan Kurs Mata Uang Valas *</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, mata_uang: 'USD', _lastEdited: 'rupiah' }));
                  }}
                  className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                    form.mata_uang === 'USD'
                      ? 'bg-amber-50 text-amber-700 border-amber-300 shadow-sm'
                      : 'border-border text-muted-foreground hover:border-accent/50'
                  }`}
                >
                  USD ($)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, mata_uang: 'EUR', _lastEdited: 'rupiah' }));
                  }}
                  className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                    form.mata_uang === 'EUR'
                      ? 'bg-blue-50 text-blue-700 border-blue-300 shadow-sm'
                      : 'border-border text-muted-foreground hover:border-accent/50'
                  }`}
                >
                  EUR (€)
                </button>
              </div>
            </div>
          )}

          {/* Harga */}
          {!dariClient && !hidePrices && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {form.mata_uang === 'USD' ? (
                    <>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Harga Beli USD ($)</label>
                      <Input type="number" step="0.01" value={form.harga_dollar} onChange={e => setForm(p => ({ ...p, harga_dollar: e.target.value, _lastEdited: 'valas' }))} placeholder="0.00" />
                    </>
                  ) : (
                    <>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Harga Beli EUR (€)</label>
                      <Input type="number" step="0.01" value={form.harga_euro} onChange={e => setForm(p => ({ ...p, harga_euro: e.target.value, _lastEdited: 'valas' }))} placeholder="0.00" />
                    </>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    {tipe === 'fullset' ? 'Harga Beli Paket (Rp) *' : 'Harga Beli Rupiah (Rp) *'}
                  </label>
                  <Input type="number" value={form.harga_rupiah} onChange={e => setForm(p => ({ ...p, harga_rupiah: e.target.value, _lastEdited: 'rupiah' }))} placeholder="0" required />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {tipe === 'fullset' ? 'Harga Jual Paket (Rp)' : 'Harga Jual Rupiah (Rp)'}
                </label>
                <Input type="number" value={form.harga_jual} onChange={e => set('harga_jual', e.target.value)} placeholder="0" />
              </div>

              {form.mata_uang === 'USD' && form.harga_dollar && form.harga_rupiah && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  💱 ${parseFloat(form.harga_dollar).toFixed(2)} × Rp {kurs.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = Rp {(parseFloat(form.harga_dollar) * kurs).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
              {form.mata_uang === 'EUR' && form.harga_euro && form.harga_rupiah && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  💱 €{parseFloat(form.harga_euro).toFixed(2)} × Rp {kursEuro.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = Rp {(parseFloat(form.harga_euro) * kursEuro).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
            </>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              {tipe === 'fullset' ? 'Stok Tersedia (pcs) *' : 'Stok (pcs)'}
            </label>
            <Input type="number" value={form.stok} onChange={e => set('stok', e.target.value)} placeholder="0" required={tipe === 'fullset'} />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Status Item *</label>
            <div className="flex gap-2">
              {[
                { v: 'aktif', l: 'Aktif' },
                { v: 'pasif', l: 'Pasif' },
                { v: 'nonaktif', l: 'Nonaktif' },
              ].map(st => (
                <button key={st.v} type="button" onClick={() => set('status', st.v)}
                  className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.status === st.v ? 'bg-primary text-primary-foreground border-primary shadow-sm' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                >
                  {st.l}
                </button>
              ))}
            </div>
          </div>

          {/* Field Catatan untuk Tutup dan Fullset */}
          {(tipe === 'tutup' || tipe === 'fullset') && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Catatan Tambahan</label>
              <Textarea 
                value={form.catatan} 
                onChange={e => set('catatan', e.target.value)} 
                placeholder="Contoh: Sudah termasuk segel plastik..." 
                className="resize-none h-20 text-sm"
              />
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button 
              type="submit" 
              disabled={mutation.isPending} 
              className={`flex-1 font-bold ${
                tipe === 'fullset' 
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                  : 'bg-accent hover:bg-accent/90 text-accent-foreground'
              }`}
            >
              {mutation.isPending ? 'Menyimpan...' : (tipe === 'fullset' ? 'Simpan Fullset' : 'Simpan')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}